import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Loader2, Copy, Check, Link as LinkIcon, FileText } from "lucide-react";
import { userService } from "../services/userService";
import { bidService } from "../services/bidService";
import { ReportFormatOption } from "../models/bid";

interface ProfileComponentProps {}

const ProfileComponent: React.FC<ProfileComponentProps> = () => {
  const { user, isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const [alias, setAlias] = useState<string>("");
  const [originalAlias, setOriginalAlias] = useState<string>("");
  const [aliasLoading, setAliasLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Report format (admin-only self-service control)
  const [isAdmin, setIsAdmin] = useState(false);
  const [reportFormat, setReportFormat] = useState<string>("");
  const [formatOptions, setFormatOptions] = useState<ReportFormatOption[]>([]);
  const [savingFormat, setSavingFormat] = useState(false);
  const [formatMessage, setFormatMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const shareableUrl = alias ? `${window.location.origin}/scope/${alias}/new` : "";

  useEffect(() => {
    if (isAuthenticated) {
      loadAlias();
      loadReportFormat();
    }
  }, [isAuthenticated]);

  const loadReportFormat = async () => {
    try {
      // Only admins manage report format from their profile.
      const token = await getAccessTokenSilently();
      const payload = JSON.parse(atob(token.split(".")[1]));
      const admin = payload["https://app.fxer.ai/role"] === "ADMIN";
      setIsAdmin(admin);
      if (!admin || !user?.sub) return;

      const data = await bidService.getReportFormat(user.sub);
      setReportFormat(data.format);
      setFormatOptions(data.availableFormats);
    } catch (err) {
      console.error("Error loading report format:", err);
    }
  };

  const handleSaveFormat = async (format: string) => {
    if (!user?.sub) return;
    setReportFormat(format);
    setSavingFormat(true);
    setFormatMessage(null);
    try {
      await bidService.setReportFormat(user.sub, format);
      setFormatMessage({ type: "success", text: "Report format saved." });
      setTimeout(() => setFormatMessage(null), 3000);
    } catch (err) {
      console.error("Error saving report format:", err);
      setFormatMessage({ type: "error", text: err instanceof Error ? err.message : "Failed to save report format" });
    } finally {
      setSavingFormat(false);
    }
  };

  const loadAlias = async () => {
    try {
      setAliasLoading(true);
      const data = await userService.getAlias();
      const fetchedAlias = data.alias || "";
      setAlias(fetchedAlias);
      setOriginalAlias(fetchedAlias);
    } catch (error) {
      console.error('Error loading alias:', error);
      setError('Failed to load alias');
    } finally {
      setAliasLoading(false);
    }
  };

  const handleSaveAlias = async () => {
    if (!alias.trim()) {
      setError('Alias cannot be empty');
      return;
    }

    // Basic validation - only alphanumeric and hyphens
    if (!/^[a-zA-Z0-9-]+$/.test(alias)) {
      setError('Alias can only contain letters, numbers, and hyphens');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      const result = await userService.setAlias(alias);
      setAlias(result.alias);
      setOriginalAlias(result.alias);
      setSuccess('Public link updated successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      console.error('Error saving alias:', error);
      setError(error.message || 'Failed to save alias');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyUrl = async () => {
    if (shareableUrl) {
      try {
        await navigator.clipboard.writeText(shareableUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (error) {
        console.error('Failed to copy:', error);
      }
    }
  };

  if (isLoading) {
    return <div>Loading ...</div>;
  }

  if (!isAuthenticated || !user) {
    return null;
  }

  const hasChanges = alias !== originalAlias;

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <div className="mb-6">
        <img src={user.picture} alt={user.name} className="w-20 h-20 rounded-full mb-4" />
        <h2 className="text-2xl font-bold">{user.name}</h2>
        <p className="text-gray-600">{user.email}</p>
        <a
          href={process.env.REACT_APP_STRIPE_CUSTOMER_PORTAL_URL || '#'}
          className="text-blue-600 hover:underline mt-2 inline-block"
        >
          Manage Subscription
        </a>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LinkIcon className="h-5 w-5" />
            Public Project Link
          </CardTitle>
        </CardHeader>
        <CardContent>
          {aliasLoading ? (
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Loading...</span>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label htmlFor="alias" className="text-sm font-medium">
                  Your Custom Alias
                </label>
                <Input
                  id="alias"
                  type="text"
                  value={alias}
                  onChange={(e) => setAlias(e.target.value)}
                  placeholder="e.g., john-renovations"
                  className="mt-1"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Choose a unique alias for your public project link. Use only letters, numbers, and hyphens.
                </p>
              </div>

              {shareableUrl && (
                <div>
                  <label className="text-sm font-medium">
                    Your Shareable URL
                  </label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      type="text"
                      value={shareableUrl}
                      readOnly
                      className="flex-1 bg-gray-50"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleCopyUrl}
                      className="flex items-center gap-2"
                    >
                      {copied ? (
                        <>
                          <Check className="h-4 w-4" />
                          Copied
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4" />
                          Copy
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {error && (
                <div className="bg-red-50 text-red-800 p-4 rounded border border-red-200">
                  {error}
                </div>
              )}

              {success && (
                <div className="bg-green-50 text-green-800 p-4 rounded border border-green-200">
                  {success}
                </div>
              )}

              <Button
                onClick={handleSaveAlias}
                disabled={saving || !hasChanges}
                className="w-full sm:w-auto"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Public Link'
                )}
              </Button>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                <p className="text-sm text-blue-900">
                  <strong>How it works:</strong> Share this link with clients or customers. They can visit it to create a project and fill out a questionnaire without needing to log in.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {isAdmin && formatOptions.length > 0 && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Report Format
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <p className="text-sm text-gray-500">
                Controls which PDF layout your bid reports use.
              </p>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <select
                  value={reportFormat}
                  onChange={(e) => handleSaveFormat(e.target.value)}
                  disabled={savingFormat}
                  className="flex-1 sm:flex-none sm:min-w-[260px] px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {formatOptions.map((opt) => (
                    <option key={opt.key} value={opt.key}>{opt.label}</option>
                  ))}
                </select>
                {savingFormat && <Loader2 className="h-4 w-4 animate-spin text-gray-500" />}
                {formatMessage && (
                  <span className={`text-sm ${formatMessage.type === "success" ? "text-green-700" : "text-red-700"}`}>
                    {formatMessage.text}
                  </span>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ProfileComponent;
